/**
 * Чистые функции проекции nestedHeaders Handsontable на видимые колонки.
 * Вынесены отдельно от exportToExcel — чтобы можно было покрыть юнит-тестами
 * без HoT-инстанса.
 */

export interface VisibleHeaderEntry {
  label: string;
  /** Колспан после проекции через visibleCols (>= 1). */
  colspan: number;
  rowspan: number;
  /** Смещение в координатах ВИДИМЫХ колонок (без учёта row-headers). */
  startVisibleCol: number;
  /** Позиция этой группы в исходном level (до проекции) — для look-up цветов. */
  originalIndexInLevel: number;
  /** Первая исходная колонка, попадающая в группу — для leaf-level look-up. */
  originalStartCol: number;
}

export interface VisibleHeaderGrid {
  levels: VisibleHeaderEntry[][];
  totalVisibleCols: number;
}

export type RawHeader = string | number | { label?: unknown; colspan?: unknown; rowspan?: unknown } | null | undefined;

export function readHeader(raw: RawHeader): { label: string; colspan: number; rowspan: number } {
  if (raw && typeof raw === 'object') {
    return {
      label: raw.label != null ? String(raw.label) : '',
      colspan: Math.max(1, Number(raw.colspan ?? 1) || 1),
      rowspan: Math.max(1, Number(raw.rowspan ?? 1) || 1),
    };
  }
  return { label: raw != null ? String(raw) : '', colspan: 1, rowspan: 1 };
}

/**
 * Проецирует nestedHeaders Handsontable через множество видимых колонок.
 *
 * Для каждой группы считается, сколько из её исходных колонок реально видимо;
 * именно это число становится новым colspan. Группы с visibleColspan=0
 * выпадают полностью.
 */
export function projectNestedHeaders(
  nestedHeaders: readonly (readonly RawHeader[])[],
  visibleCols: readonly number[]
): VisibleHeaderGrid {
  const visibleSet = new Set<number>(visibleCols);
  const visibleIndexByOriginal = new Map<number, number>();
  visibleCols.forEach((orig, idx) => visibleIndexByOriginal.set(orig, idx));

  const levels: VisibleHeaderEntry[][] = [];

  for (const rawLevel of nestedHeaders) {
    if (!Array.isArray(rawLevel)) {
      levels.push([]);
      continue;
    }

    const levelEntries: VisibleHeaderEntry[] = [];
    let originalCol = 0;
    let originalIndexInLevel = 0;

    for (const raw of rawLevel) {
      const { label, colspan, rowspan } = readHeader(raw);

      let visibleColspan = 0;
      let firstVisibleOriginal = -1;
      for (let k = 0; k < colspan; k++) {
        const oc = originalCol + k;
        if (visibleSet.has(oc)) {
          visibleColspan++;
          if (firstVisibleOriginal === -1) firstVisibleOriginal = oc;
        }
      }

      if (visibleColspan > 0) {
        levelEntries.push({
          label,
          colspan: visibleColspan,
          rowspan,
          startVisibleCol: visibleIndexByOriginal.get(firstVisibleOriginal) ?? 0,
          originalIndexInLevel,
          originalStartCol: originalCol,
        });
      }

      originalCol += colspan;
      originalIndexInLevel++;
    }

    levels.push(levelEntries);
  }

  return { levels, totalVisibleCols: visibleCols.length };
}

/**
 * Превращает grid в плоские строки заголовков (по одной строке на уровень).
 * Метка пишется в первую ячейку группы; остальные пустые — Excel при merge
 * берёт значение из верхне-левой, а без merge пустоты не «дублируют» подпись.
 */
export function buildHeaderRows(
  grid: VisibleHeaderGrid,
  rowHeaderColumns: number
): string[][] {
  const width = grid.totalVisibleCols + rowHeaderColumns;
  const rows: string[][] = grid.levels.map(() => new Array<string>(width).fill(''));

  grid.levels.forEach((entries, levelIdx) => {
    for (const entry of entries) {
      rows[levelIdx][entry.startVisibleCol + rowHeaderColumns] = entry.label;
    }
  });

  return rows;
}

export interface MergeRange {
  s: { r: number; c: number };
  e: { r: number; c: number };
}

export function buildMerges(grid: VisibleHeaderGrid, rowHeaderColumns: number): MergeRange[] {
  const merges: MergeRange[] = [];
  grid.levels.forEach((entries, levelIdx) => {
    for (const entry of entries) {
      if (entry.colspan === 1 && entry.rowspan === 1) continue;
      const startCol = entry.startVisibleCol + rowHeaderColumns;
      merges.push({
        s: { r: levelIdx, c: startCol },
        e: { r: levelIdx + entry.rowspan - 1, c: startCol + entry.colspan - 1 },
      });
    }
  });
  return merges;
}
